import { SPOT_GRID_MAX_ZOOM, SPOT_GRID_MIN_ZOOM } from './spotGridZoomConstants';

export function clampZoom(scale: number): number {
  'worklet';
  return Math.min(SPOT_GRID_MAX_ZOOM, Math.max(SPOT_GRID_MIN_ZOOM, scale));
}

function clampAxis(
  translate: number,
  viewportSize: number,
  scaledSize: number,
  alignToStartWhenFits: boolean
): number {
  'worklet';
  if (scaledSize <= viewportSize) {
    return alignToStartWhenFits ? 0 : (viewportSize - scaledSize) / 2;
  }
  const min = viewportSize - scaledSize;
  const max = 0;
  return Math.min(max, Math.max(min, translate));
}

function getAxisDefaultTranslation(
  viewportSize: number,
  scaledSize: number,
  alignToStartWhenOverflow: boolean,
  alignToStartWhenFits: boolean
): number {
  'worklet';
  if (scaledSize <= viewportSize) {
    return alignToStartWhenFits ? 0 : (viewportSize - scaledSize) / 2;
  }
  if (alignToStartWhenOverflow) {
    return 0;
  }
  return (viewportSize - scaledSize) / 2;
}

/**
 * Default pan offset when the grid loads or resets.
 * - Horizontal: centered when wider than the viewport (legacy ScrollView behavior).
 * - Vertical: top-aligned when taller than the viewport so the first row is fully visible.
 */
export function getDefaultPanTranslation(
  scale: number,
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  'worklet';
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return { x: 0, y: 0 };
  }

  let x = 0;
  let y = 0;
  if (contentWidth > 0) {
    x = getAxisDefaultTranslation(viewportWidth, contentWidth * scale, false, false);
  }
  if (contentHeight > 0) {
    y = getAxisDefaultTranslation(viewportHeight, contentHeight * scale, true, true);
  }
  return { x, y };
}

/**
 * Keeps panned content inside the viewport so the zoomed grid cannot be dragged fully off-screen.
 * When content fits on an axis, it stays centered; when larger, panning is clamped to the edges.
 */
export function clampPanTranslation(
  translateX: number,
  translateY: number,
  scale: number,
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  'worklet';
  if (viewportWidth <= 0 || viewportHeight <= 0) {
    return { x: 0, y: 0 };
  }

  let x = translateX;
  let y = translateY;
  if (contentWidth > 0) {
    x = clampAxis(translateX, viewportWidth, contentWidth * scale, false);
  }
  if (contentHeight > 0) {
    y = clampAxis(translateY, viewportHeight, contentHeight * scale, true);
  }
  return { x, y };
}

/**
 * Fixed viewport height for pinch-zoom mode.
 * Uses the same cap at 1× and when zoomed so the layout does not jump on pinch and the legend
 * slot stays stable. Scaled content pans inside this viewport; in embed mode the parent page
 * scrolls when the full map (grid + legend) is taller than the slot.
 */
export type PanGestureActivation = 'activate' | 'fail' | 'wait';

/**
 * Manual pan activation: only claim the touch on an axis that can pan, and fail when the user
 * drags on a non-pannable axis (e.g. vertical page scroll over a wide 1× grid).
 */
export function resolvePanGestureActivation(
  dx: number,
  dy: number,
  canPanX: boolean,
  canPanY: boolean,
  activationOffsetPx: number
): PanGestureActivation {
  'worklet';
  if (!canPanX && !canPanY) {
    return 'fail';
  }

  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  if (!canPanY && canPanX) {
    if (absDy >= activationOffsetPx && absDy > absDx) return 'fail';
    if (absDx >= activationOffsetPx) return 'activate';
    return 'wait';
  }

  if (!canPanX && canPanY) {
    if (absDx >= activationOffsetPx && absDx > absDy) return 'fail';
    if (absDy >= activationOffsetPx) return 'activate';
    return 'wait';
  }

  if (absDx < activationOffsetPx && absDy < activationOffsetPx) {
    return 'wait';
  }

  if (absDx >= absDy) {
    return absDx >= activationOffsetPx ? 'activate' : 'fail';
  }

  return absDy >= activationOffsetPx ? 'activate' : 'fail';
}

export function getZoomViewportHeight(
  contentHeight: number,
  maxSlotHeight: number,
  embedInPageScroll = false
): number | undefined {
  if (contentHeight <= 0) return undefined;
  if (maxSlotHeight <= 0) {
    return embedInPageScroll ? contentHeight : undefined;
  }
  // In embed mode, let the grid keep its natural height when it exceeds the slot so the
  // parent ScrollView can scroll to rows below instead of clipping inside overflow:hidden.
  if (embedInPageScroll && contentHeight > maxSlotHeight) {
    return contentHeight;
  }
  return Math.min(contentHeight, maxSlotHeight);
}
